import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Button,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Card,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import EventIcon from '@mui/icons-material/Event';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import ScheduleIcon from '@mui/icons-material/Schedule';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AddIcon from '@mui/icons-material/Add';
import ApiService from '../../../../services/api';
import logger from '../../../../utils/logger';
import { formatDateTimeSeparate } from '../../../../utils/dateUtils';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { useAdminRole } from '../../../../contexts/AdminRoleContext';

const Container = styled(Box)(({ theme }) => ({
  padding: '24px',
  backgroundColor: '#f5f7fa',
  minHeight: '100vh',
}));

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
  flexWrap: 'wrap',
  gap: '16px',
}));

const TitleSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
}));

const TitleIcon = styled(Box)(({ theme }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
}));

const SearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#fff',
    '&:hover fieldset': {
      borderColor: '#3b82f6',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3b82f6',
    },
  },
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '16px',
  marginBottom: '24px',
}));

const StatCard = styled(Card)(({ theme }) => ({
  padding: '20px',
  borderRadius: '16px',
  background: '#fff',
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
  },
}));

const StatIconBox = styled(Box)(({ bgcolor }) => ({
  width: '50px',
  height: '50px',
  borderRadius: '12px',
  background: bgcolor || 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
}));

const TableContainerStyled = styled(TableContainer)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  overflow: 'hidden',
}));

const Th = styled(TableCell)(({ theme }) => ({
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  color: '#ffffff',
  fontWeight: 600,
  padding: '16px',
  fontSize: '14px',
  whiteSpace: 'nowrap',
}));

const Td = styled(TableCell)(({ theme }) => ({
  padding: '14px 16px',
  borderBottom: '1px solid #f0f0f0',
  fontSize: '14px',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: '#f0fdf4',
  },
}));

const StatusChip = styled(Chip)(({ status }) => {
  const colors = {
    'COMPLETED': { bg: '#dcfce7', color: '#166534' },
    'completed': { bg: '#dcfce7', color: '#166534' },
    'scheduled': { bg: '#dbeafe', color: '#1e40af' },
    'RESCHEDULE': { bg: '#f3e8ff', color: '#7c3aed' },
    'NOT AVAILABLE': { bg: '#fee2e2', color: '#dc2626' },
    'NOT INTERESTED': { bg: '#fef3c7', color: '#92400e' },
    'default': { bg: '#f3f4f6', color: '#4b5563' },
  };
  const normalize = (s) => (s || '').toString(); // Keep case for direct lookup or use upper
  const statusStr = normalize(status);
  const colorSet = colors[statusStr] || colors[statusStr.toLowerCase()] || colors.default;
  return {
    backgroundColor: colorSet.bg,
    color: colorSet.color,
    fontWeight: 600,
    fontSize: '12px',
  };
});

const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: '8px',
  '&:hover': {
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
  },
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '24px',
  padding: '0 8px',
}));

const PaginationButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: '8px',
  padding: '8px 16px',
  fontWeight: 500,
}));

const MeetingListing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [meetings, setMeetings] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [meetingToDelete, setMeetingToDelete] = useState(null);

  // Role-based access
  const { getFilterParams, isSalesTeam, getRoleName } = useAdminRole();

  const toTitle = (s) => {
    if (s === null || s === undefined) return '';
    const v = String(s);
    const vs = v.toLowerCase();
    if (v === 'COMPLETED' || vs === 'completed' || v === '1' || vs === 'true' || vs === 'closed_won') return 'COMPLETED';
    if (v === 'RESCHEDULE' || vs === 'rescheduled') return 'RESCHEDULE';
    if (v === 'NOT AVAILABLE' || vs === 'not_available') return 'NOT AVAILABLE';
    if (v === 'NOT INTERESTED' || vs === 'not_interested') return 'NOT INTERESTED';
    if (vs === 'scheduled') return 'Scheduled';
    return v;
  };

  const fmtDate = (dt) => {
    return formatDateTimeSeparate(dt);
  };

  useEffect(() => {
    const fetchMeetings = async () => {
      setLoading(true);
      setError('');
      try {
        // Add role-based filtering params
        const roleParams = getFilterParams();
        const url = `/admin/meeting/get-meeting?page=1&pageSize=100${roleParams}`;

        const res = await ApiService.get(url);
        const root = res?.data ?? res;
        let list = [];
        if (Array.isArray(root)) list = root;
        else if (Array.isArray(root?.data)) list = root.data;
        else if (Array.isArray(root?.data?.meetings)) list = root.data.meetings;
        else if (Array.isArray(root?.meetings)) list = root.meetings;

        const normalized = list.map((m) => {
          const id = m.id || m.meeting_id || m._id || m.uuid;
          const dt = m.date_time || m.meeting_date || m.createdAt || m.updatedAt;
          const { time, date } = fmtDate(dt);
          const lead = m.leadDetails || m.lead || m.lead_details || {};
          const emp = m.employeeDetails || m.employee || m.bdm || {};
          const tel = m.telly_meeting === true || m.telly_meeting === 1 || m.telly_meeting === '1';
          const businessName = lead.business_name || m.clientDetails?.full_name || m.userDetails?.full_name || '—';
          const phone = lead.phone_number || lead.alt_number || m.phone || '—';
          const contact = lead.contact_person || m.contact_person || '—';
          const bdm = [emp.first_name, emp.last_name].filter(Boolean).join(' ') || emp.full_name || emp.name || '—';
          const status = toTitle(m.status);
          const location = lead.city || lead.state || lead.country || '—';
          return { id, businessName, time, date, phone, contact, tele: tel, bdm, status, location };
        });

        setMeetings(normalized);
      } catch (e) {
        logger.error('Failed to fetch meetings:', e);
        const raw = e?.response?.data || e?.body || e?.message;
        const msg = typeof raw === 'string' ? raw : (raw?.message || raw?.error || 'Failed to load meetings');
        setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      } finally {
        setLoading(false);
      }
    };
    fetchMeetings();
  }, [getFilterParams]);

  const normalizeForCompare = (v) => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v.toLowerCase();
    return String(v).toLowerCase();
  };

  const filteredMeetings = meetings.filter(meeting => {
    if (!searchQuery) return true;
    const q = normalizeForCompare(searchQuery);
    return (
      normalizeForCompare(meeting.businessName).includes(q) ||
      normalizeForCompare(meeting.bdm).includes(q) ||
      normalizeForCompare(meeting.status).includes(q) ||
      normalizeForCompare(meeting.location).includes(q)
    );
  });

  const completedCount = meetings.filter(m => m.status === 'COMPLETED').length;
  const scheduledCount = meetings.filter(m => m.status === 'Scheduled' || m.status.toLowerCase() === 'scheduled').length;
  const teleCount = meetings.filter(m => m.tele).length;

  const handleDeleteClick = (meeting) => {
    setMeetingToDelete(meeting);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!meetingToDelete?.id) {
      setDeleteDialogOpen(false);
      setMeetingToDelete(null);
      return;
    }
    try {
      await ApiService.delete(`/admin/meeting/delete-meeting/${meetingToDelete.id}`);
      setMeetings(prev => prev.filter(m => m.id !== meetingToDelete.id));
      setDeleteDialogOpen(false);
      setMeetingToDelete(null);
    } catch (e) {
      logger.error('Failed to delete meeting', e);
      setError('Failed to delete meeting. Please try again.');
      setDeleteDialogOpen(false);
      setMeetingToDelete(null);
    }
  };

  return (
    <Container>
      {/* Header */}
      <Header>
        <TitleSection>
          <TitleIcon>
            <EventIcon sx={{ fontSize: 26 }} />
          </TitleIcon>
          <Box>
            <Typography variant="h4" fontWeight={700} color="#1f2937">Meetings</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and track all scheduled meetings
            </Typography>
          </Box>
        </TitleSection>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <SearchField
            placeholder="Search meetings..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: '#9ca3af', mr: 1 }} />,
            }}
            sx={{ width: '280px' }}
          />

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin-dashboard/add-meeting')}
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              },
            }}
          >
            Add Meeting
          </Button>
        </Box>
      </Header>


      {error && (
        <Box sx={{ bgcolor: '#fef2f2', color: '#dc2626', p: 2, borderRadius: '10px', mb: 3 }}>
          {error}
        </Box>
      )}

      {/* Table */}
      <TableContainerStyled component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <Th>Business</Th>
              <Th>Date & Time</Th>
              <Th>Contact</Th>
              <Th>Phone</Th>
              <Th>BDM</Th>
              <Th>Status</Th>
              <Th>Location</Th>
              <Th align="center">Actions</Th>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <StyledTableRow>
                <Td colSpan={8} align="center">
                  <Typography color="text.secondary">Loading meetings...</Typography>
                </Td>
              </StyledTableRow>
            ) : filteredMeetings.length === 0 ? (
              <StyledTableRow>
                <Td colSpan={8} align="center">
                  <Typography color="text.secondary">No meetings found</Typography>
                </Td>
              </StyledTableRow>
            ) : (
              filteredMeetings.map((meeting) => (
                <StyledTableRow
                  key={meeting.id || Math.random()}
                  onClick={() => meeting.id && navigate(`/admin-dashboard/meeting/${meeting.id}`)}
                  sx={{ cursor: 'pointer', transition: 'all 0.2s', '&:hover': { backgroundColor: '#f8fafc', transform: 'translateX(4px)' } }}
                >
                  <Td>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '8px',
                          bgcolor: '#f0fdf4',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <BusinessIcon sx={{ color: '#10b981', fontSize: 18 }} />
                      </Box>
                      <Typography fontWeight={600} color="#1f2937">
                        {meeting.businessName}
                      </Typography>
                    </Box>
                  </Td>
                  <Td>
                    <Box>
                      <Typography fontWeight={600} color="#1f2937">{meeting.time}</Typography>
                      <Typography variant="caption" color="text.secondary">{meeting.date}</Typography>
                    </Box>
                  </Td>
                  <Td>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon sx={{ color: '#9ca3af', fontSize: 16 }} />
                      {meeting.contact}
                    </Box>
                  </Td>
                  <Td>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon sx={{ color: '#9ca3af', fontSize: 16 }} />
                      {meeting.phone}
                    </Box>
                  </Td>
                  <Td>
                    <Chip
                      label={meeting.bdm}
                      size="small"
                      sx={{ bgcolor: '#e0e7ff', color: '#4338ca', fontWeight: 500 }}
                    />
                  </Td>
                  <Td>
                    <StatusChip label={meeting.status} status={meeting.status} size="small" />
                  </Td>
                  <Td>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOnIcon sx={{ color: '#9ca3af', fontSize: 16 }} />
                      {meeting.location}
                    </Box>
                  </Td>
                  <Td align="center">
                    <Tooltip title="View Details">
                      <ActionButton
                        onClick={(e) => { e.stopPropagation(); meeting.id && navigate(`/admin-dashboard/meeting/${meeting.id}`); }}
                        sx={{ color: '#3b82f6' }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </ActionButton>
                    </Tooltip>
                    <Tooltip title="Delete Meeting">
                      <ActionButton
                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(meeting); }}
                        sx={{ color: '#e53e3e' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </ActionButton>
                    </Tooltip>
                  </Td>
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainerStyled>

      {/* Pagination */}
      <PaginationContainer>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredMeetings.length} of {meetings.length} meetings
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <PaginationButton variant="outlined" size="small">
            Previous
          </PaginationButton>
          <PaginationButton
            variant="contained"
            size="small"
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              },
            }}
          >
            Next
          </PaginationButton>
        </Box>
      </PaginationContainer>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Meeting</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the meeting for "{meetingToDelete?.businessName}" scheduled on {meetingToDelete?.date} {meetingToDelete?.time}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleDeleteConfirm}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MeetingListing;















